// Dessiner graphe avec canvas

// Calcule un facteur d'échelle par paliers en fonction de la largeur
// réelle du canvas (qui correspond à la largeur de l'écran, le graphe
// étant en pleine largeur sur mobile/tablette).
//
// >= 800px            (desktop / grande tablette) -> échelle normale
// 452px à 799px       (tablette / petit écran)     -> réduction modérée
// 380px à 451px       (grand téléphone)            -> réduction plus forte
// < 380px             (petit téléphone)            -> réduction maximale
export const getNodeScale = (canvasWidth) => {
    if (canvasWidth >= 800) return 1
    if (canvasWidth >= 452) return 0.75
    if (canvasWidth >= 380) return 0.58
    return 0.42
}

// Modifier la fonction drawGraph pour accepter les chemins multiples
export const drawGraph = (
    canvasRef, nodes, edges, selectedNode, secondSelectedNode, path,
    startNode, endNode, showSteps, steps, afficheMin, allPaths = null
) => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    
    // Ajuster la taille du canvas
    canvas.width = rect.width
    canvas.height = rect.height

    // Facteur d'échelle pour adapter sommets/textes à la taille de l'écran
    const scale = getNodeScale(canvas.width)
    
    // Effacer le canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Dessiner les arêtes
    drawEdges(ctx, edges, nodes, path, afficheMin, allPaths, scale)

    // Dessiner les sommets
    drawNodes(
        ctx, nodes, selectedNode, secondSelectedNode, startNode, endNode,
        path, showSteps, steps, afficheMin, allPaths, scale
    )
}

// Modifier la fonction drawEdges pour supporter les chemins multiples
const drawEdges = (
    ctx, edges, nodes, path, afficheMin, allPaths = null, scale = 1
) => {
    // Rayon du sommet (doit correspondre à celui utilisé dans drawNodes)
    const nodeRadius = 20 * scale
    // Floors abaissés pour permettre un vrai rétrécissement sur petits écrans
    const weightCircleRadius = Math.max(6, 12 * scale)
    const weightFontSize = Math.max(7, Math.round(12 * scale))

    edges.forEach((edge) => {
      const sourceNode = nodes.find(node => node.id === edge.source)
      const targetNode = nodes.find(node => node.id === edge.target)
      
      if (sourceNode && targetNode) {
        // Vérifier si une arête opposée existe
        const oppositeEdge = edges.find(e => 
          e.source === edge.target && e.target === edge.source
        )
        
        // Calculer l'angle de la ligne
        const dx = targetNode.x - sourceNode.x
        const dy = targetNode.y - sourceNode.y
        const angle = Math.atan2(dy, dx)
        
        // Ajuster les points de départ et d'arrivée pour éviter de dessiner sur les nœuds
        let startX, startY, endX, endY
        
        // Si une arête bidirectionnelle existe, décaler les deux arêtes
        if (oppositeEdge) {
          const offset = 8 * scale // Décalage pour les arêtes bidirectionnelles
          const perpAngle = angle + Math.PI / 2
          const offsetX = offset * Math.cos(perpAngle)
          const offsetY = offset * Math.sin(perpAngle)
          
          startX = sourceNode.x + Math.cos(angle) * nodeRadius + offsetX
          startY = sourceNode.y + Math.sin(angle) * nodeRadius + offsetY
          endX = targetNode.x - Math.cos(angle) * nodeRadius + offsetX
          endY = targetNode.y - Math.sin(angle) * nodeRadius + offsetY
        } else {
          startX = sourceNode.x + Math.cos(angle) * nodeRadius
          startY = sourceNode.y + Math.sin(angle) * nodeRadius
          endX = targetNode.x - Math.cos(angle) * nodeRadius
          endY = targetNode.y - Math.sin(angle) * nodeRadius
        }
        
        // Dessiner la ligne
        ctx.beginPath()
        ctx.moveTo(startX, startY)
        ctx.lineTo(endX, endY)
        
        // Vérifier si cette arête fait partie d'un chemin minimal
        let isInPath = false
        
        // Vérifier dans le chemin principal
        isInPath = path.some(
          (segment, index) => 
            index < path.length - 1 && 
            segment.id === sourceNode.id && 
            path[index + 1].id === targetNode.id
        )
        
        // Si pas dans le chemin principal, vérifier dans tous les chemins (si multiples)
        if (!isInPath && allPaths && allPaths.length > 1) {
          isInPath = allPaths.some(singlePath => 
            singlePath.some((segment, index) => 
              index < singlePath.length - 1 && 
              segment.id === sourceNode.id && 
              singlePath[index + 1].id === targetNode.id
            )
          )
        }
        
        if (isInPath) {
          // Changer la couleur en fonction de afficheMin
          if (afficheMin) {
            ctx.strokeStyle = 'rgb(30, 123, 211)' // Bleu pour minimisation
          } else {
            ctx.strokeStyle = 'rgb(55, 196, 20)' // Vert pour maximisation
          }
          ctx.lineWidth = Math.max(1, 2 * scale)
        } else {
          ctx.strokeStyle = 'rgba(19, 19, 19, 0.918)'
          ctx.lineWidth = Math.max(1, 2 * scale)
        }
        
        ctx.stroke()
        
        // Dessiner la flèche (pointe triangulaire)
        const headLength = Math.max(6, 12 * scale) // à l'échelle, avec floor
        
        // Points pour dessiner le triangle
        const tip = { x: endX, y: endY }
        const base1 = {
          x: endX - headLength * Math.cos(angle - Math.PI/8),
          y: endY - headLength * Math.sin(angle - Math.PI/8)
        }
        const base2 = {
          x: endX - headLength * Math.cos(angle + Math.PI/8),
          y: endY - headLength * Math.sin(angle + Math.PI/8)
        }
        
        // Dessiner la pointe triangulaire
        ctx.beginPath()
        ctx.moveTo(tip.x, tip.y)
        ctx.lineTo(base1.x, base1.y)
        ctx.lineTo(base2.x, base2.y)
        ctx.closePath()
        
        if (isInPath) {
          if (afficheMin) {
            ctx.fillStyle = 'rgb(30, 123, 211)' // Bleu pour minimisation
          } else {
            ctx.fillStyle = 'rgb(55, 196, 20)' // Vert pour maximisation
          }
        } else {
          ctx.fillStyle = 'rgba(19, 19, 19, 0.918)'
        }
        
        ctx.fill()
        
        // Ajuster la position du poids
        const midX = (startX + endX) / 2
        const midY = (startY + endY) / 2
        
        // Décalage pour le poids si arête bidirectionnelle
        let weightOffsetX = 0
        let weightOffsetY = 0
        
        if (oppositeEdge) {
          const perpAngle = angle + Math.PI / 2
          const offset = 16 * scale
          weightOffsetX = offset * Math.cos(perpAngle)
          weightOffsetY = offset * Math.sin(perpAngle)
        }
        
        // Afficher la distance de l'arête
        ctx.fillStyle = 'white'
        ctx.beginPath()
        ctx.arc(midX + weightOffsetX, midY + weightOffsetY, weightCircleRadius, 0, 2 * Math.PI)
        ctx.fill()
        
        ctx.fillStyle = 'black'
        ctx.font = `${weightFontSize}px Arial`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(edge.weight, midX + weightOffsetX, midY + weightOffsetY)
      }
    })
}

// Modifier la fonction drawNodes pour supporter les chemins multiples
// Dessiner les sommets
const drawNodes = (
    ctx, nodes, selectedNode, secondSelectedNode, startNode, endNode,
    path, showSteps, steps, afficheMin, allPaths = null, scale = 1
) => {
    const nodeRadius = 20 * scale
    // Floors abaissés pour permettre un vrai rétrécissement sur petits écrans
    const nameFontSize = Math.max(8, Math.round(19 * scale))
    const lambdaFontSize = Math.max(7, Math.round(12 * scale))
    const lambdaOffsetY = 25 * scale

    nodes.forEach((node) => {
      ctx.beginPath()
      ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI)
      
      // Vérifier si le nœud fait partie d'un chemin minimal
      let isInPath = path.includes(node)
      
      if (!isInPath && allPaths && allPaths.length > 1) {
        isInPath = allPaths.some(singlePath => singlePath.includes(node))
      }
      
      // Définir la couleur en fonction de la sélection
      if (node === selectedNode || node === secondSelectedNode) {
        ctx.fillStyle = 'orange'
      } else if (node === startNode) {
        ctx.fillStyle = 'rgb(212, 209, 209)'
      } else if (node === endNode) {
        ctx.fillStyle = 'rgb(212, 209, 209)'
      } else if (isInPath) {
        if (afficheMin) {
          ctx.fillStyle = 'rgb(30, 123, 211)' // Bleu pour minimisation
        } else {
          ctx.fillStyle = 'rgb(55, 196, 20)' // Vert pour maximisation
        }
      } else {
        ctx.fillStyle = 'white'
      }
      
      ctx.fill()
      ctx.strokeStyle = 'rgba(19, 19, 19, 0.918)'
      ctx.lineWidth = Math.max(0.7, 1 * scale)
      ctx.stroke()
      
      // Afficher le nom du sommet
      ctx.fillStyle = 'rgba(19, 19, 19, 0.918)'
      ctx.font = `${nameFontSize}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(node.name, node.x, node.y)
      
      // Afficher la valeur λ si le sommet est marqué et showSteps est vrai
      if (showSteps) {
        const currentStep = steps[steps.length - 1] // Prendre la dernière étape
        if (currentStep.markedNodes.has(node.id) && currentStep.lambda[node.id] !== undefined) {
          ctx.fillStyle = 'red'
          ctx.font = `${lambdaFontSize}px Arial`
          ctx.textAlign = 'center'
          // Afficher λ(nom) = valeur avec un espace après λ
          ctx.fillText(`λ(${node.name}) = ${currentStep.lambda[node.id]}`, node.x, node.y - lambdaOffsetY)
        }
      }
    })
}